import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alertService } from '../services/api';
import { Alert } from '../types';

interface AlertDrawerProps {
  open: boolean;
  onClose: () => void;
  onAlertAcknowledged?: () => void;
}

const AlertDrawer: React.FC<AlertDrawerProps> = ({ open, onClose, onAlertAcknowledged }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadAlerts();
    }
  }, [open]);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const data = await alertService.getRecent(100);
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await alertService.acknowledge(id);
      setAlerts(prev => prev.map(alert => 
        alert.id === id ? { ...alert, acknowledged: true } : alert
      ));
      if (onAlertAcknowledged) {
        onAlertAcknowledged();
      }
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#DC2626';
      case 'high': return '#EA580C';
      case 'medium': return '#F59E0B';
      case 'low': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          backgroundColor: '#F9FAFB',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Alert Notifications
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No alerts found
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {alerts.map((alert, index) => (
              <React.Fragment key={alert.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    bgcolor: alert.acknowledged ? 'transparent' : 'white',
                    borderRadius: 1,
                    mb: 1,
                    p: 2,
                    border: '1px solid',
                    borderColor: alert.acknowledged ? '#E5E7EB' : getSeverityColor(alert.severity),
                    opacity: alert.acknowledged ? 0.6 : 1,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                    <Chip
                      label={alert.severity.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: getSeverityColor(alert.severity),
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                    {alert.acknowledged && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label="Acknowledged"
                        size="small"
                        sx={{
                          bgcolor: '#10B981',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {alert.alert_type.replace(/_/g, ' ').toUpperCase()}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {alert.message}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    {formatDate(alert.triggered_at)}
                  </Typography>

                  {!alert.acknowledged && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleAcknowledge(alert.id)}
                      sx={{ mt: 1 }}
                    >
                      Acknowledge
                    </Button>
                  )}
                </ListItem>
                {index < alerts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default AlertDrawer;
